import { Typography } from '@mui/material';
import { CSSProperties, useState } from 'react';
import {
  Map as ReactMapGl,
  GeolocateControl,
  Source,
  GeoJSONSourceRaw,
  Layer,
  NavigationControl,
  Popup,
} from 'react-map-gl';

import { appConfig } from '~/common/config';

import { useMap, UseMapOptions } from './Map.hooks';
import { Root } from './Map.styled';
import { MapSourceId } from './Map.types';
import { jitterData, POPUP_OFFSETS } from './Map.util';
import { UserLocationMarker } from './UserLocationMarker/UserLocationMarker';

const MAPBOX_TOKEN = appConfig.mapbox.token;

const style: CSSProperties = {
  width: '100%',
  height: '100%',
};

export interface MapProps extends UseMapOptions {
  mapStyle: string;
  sources?: {
    data: GeoJSONSourceRaw['data'] | null;
    id: string;
    cluster?: boolean;
    clusterMaxZoom?: number;
    clusterRadius?: number;
    clusterMinPoints?: number;
  }[];
  loading?: boolean;
}

export const Map = ({ sources, viewState, mapStyle, onSelect, onViewStateSelect, markerVariants }: MapProps) => {
  const {
    layers,
    interactiveLayerIds,
    userLocation,
    popup,
    mapRef,
    rootRef,
    onClick,
    onLoad,
    onGeolocateError,
    onGeolocate,
    onMouseEnter,
    onMouseLeave,
  } = useMap({
    markerVariants,
    viewState,
    onSelect,
    onViewStateSelect,
  });

  let transformedData: GeoJSON.FeatureCollection<GeoJSON.Geometry>;

  const [isDataTransformed, setIsDataTransformed] = useState<boolean>(false);

  return (
    <Root ref={rootRef}>
      <ReactMapGl
        {...viewState}
        onMove={(event) => onViewStateSelect(event.viewState)}
        style={style}
        mapStyle={mapStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        attributionControl={false}
        interactiveLayerIds={interactiveLayerIds}
        onClick={onClick}
        ref={mapRef}
        onLoad={onLoad}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {sources &&
          sources.map((source) => {
            if (!source.data) return undefined;

            if (source.id === MapSourceId.supplierGeoData && !isDataTransformed) {
              transformedData = jitterData(source.data as GeoJSON.FeatureCollection<GeoJSON.Geometry>);
              setIsDataTransformed(true);
            }

            return (
              <Source
                key={source.id}
                type="geojson"
                data={source.data || transformedData}
                cluster={source.cluster ?? false}
                clusterMaxZoom={source.clusterMaxZoom ?? 0}
                clusterRadius={source.clusterRadius ?? 0}
                clusterMinPoints={source.clusterMinPoints ?? 0}
                // clusterProperties={{
                //   // keep separate counts for each supplier type
                //   // same as:  potentialSuppliers += isPotentialSupplier ? 1 : 0
                //   potentialSuppliers: ['+', ['case', filterPotentialSupplier, 1, 0]],
                //   provenSuppliers: ['+', ['case', filterProvenSupplier, 1, 0]],
                // }}
              >
                {layers.map((layer) => layer.groupId === source.id && <Layer key={layer.id} {...layer} />)}
              </Source>
            );
          })}

        <GeolocateControl
          position="top-left"
          showUserLocation={false}
          onGeolocate={onGeolocate}
          onError={onGeolocateError}
        />

        <NavigationControl position="top-left" showCompass={false} />

        {userLocation ? <UserLocationMarker location={userLocation} /> : null}

        {popup && (
          <Popup
            longitude={popup.lng}
            latitude={popup.lat}
            offset={POPUP_OFFSETS}
            focusAfterOpen={false}
            closeOnClick={false}
            closeButton={false}
          >
            <Typography sx={{ marginBottom: '-5px' }} variant="body2">
              {popup.name}
            </Typography>
          </Popup>
        )}
      </ReactMapGl>
    </Root>
  );
};
